<script setup lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'
import { storeToRefs } from 'pinia'

import { BtnOrange } from '~/components/ui/Buttons/'

import { useCuratorProfilePreviousButton } from '~/composables/useCuratorProfilePreviousButton'

import { useMiscResizeStore } from '~/stores/miscResize'
import { useUserStore } from '~/stores/user'

import { capitalize } from '~/utils/strings'

import { Breakpoints } from '~/enums/breakpoints'

import type { Influencer } from '~/types/influencer'

type Props = {
  modelValue?: boolean
  influencer: Influencer
  showAbout: boolean
  hasInteraction: boolean
  showInteraction: boolean
  userIsOwner: boolean
}

type Emits = {
  toggle: []
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()
const localePath = useLocalePath()
const { handleChevronClick } = useCuratorProfilePreviousButton()

const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { IS_BAND, IS_LOGGED_IN } = storeToRefs(useUserStore())

const isBookmarkDisplayed = computed<boolean>(() => {
  return (
    !props.userIsOwner &&
    IS_BAND.value &&
    SCREEN_WIDTH.value >= Breakpoints.md &&
    !props.influencer?.is_scout &&
    props.influencer?.visible
  )
})

const isInteractionsTabDisplayed = computed<boolean>(() => {
  return IS_LOGGED_IN.value && IS_BAND.value && props.hasInteraction
})

const interactionSelectorText = computed<string>(() => {
  let result

  if (SCREEN_WIDTH.value < Breakpoints.sm) {
    result = t(`influencer.profile.slug.profile.nav02`)
  } else if (SCREEN_WIDTH.value < Breakpoints.md) {
    result = t(`influencer.profile.slug.profile.nav03`)
  } else {
    result = t(`influencer.profile.slug.profile.nav04`, {
      name: props.influencer.entity,
    })
  }

  return capitalize(result)
})

async function leaveLink() {
  if (props.userIsOwner) {
    await navigateTo(
      localePath(`/influencer/profile/${props.influencer.slug}/edit/`),
    )
  }
}
</script>

<template>
  <div class="tw-bg-white tw-shadow">
    <!-- ClientOnly used to keep dynamic content out of cdn cache -->
    <ClientOnly>
      <template #fallback><div class="tw-h-18 lg:tw-h-[88px]"></div></template>
      <div class="mainParentContainer tw-relative !tw-flex tw-items-center">
        <Transition name="fade" mode="out-in">
          <button
            v-if="
              IS_LOGGED_IN && !userIsOwner && SCREEN_WIDTH >= Breakpoints.lg
            "
            type="button"
            :aria-label="$t('common.goBack')"
            class="tw-absolute -tw-left-5 tw-top-1/2 tw-flex tw-size-6 -tw-translate-y-1/2 tw-items-center"
            @click="handleChevronClick"
          >
            <UIIcon
              name="fa6-solid:chevron-left"
              class="tw-text-icon-discrete2"
              aria-hidden
            />
          </button>
        </Transition>
        <div
          class="tw-flex tw-size-full tw-items-center lg:tw-ml-[184px]"
          :class="{
            'tw-justify-center tw-py-4 lg:tw-justify-end': userIsOwner,
          }"
        >
          <div v-if="!userIsOwner" class="tw-flex tw-w-full">
            <div
              class="tw-relative tw-flex tw-w-full tw-items-center lg:tw-w-auto"
            >
              <button
                class="tw-inline-block tw-w-full tw-cursor-pointer tw-p-6 tw-text-center lg:tw-w-auto lg:tw-py-8"
                :class="{ 'tw-cursor-default': !hasInteraction }"
                type="button"
                @click="emit('toggle')"
              >
                <span
                  class="tw-text-body tw-inline-block"
                  :class="{ 'tw-text-medium tw-text-orange-500': showAbout }"
                  >{{ t('influencer.profile.slug.profile.nav00') }}</span
                >
              </button>
              <Transition name="scale" mode="out-in">
                <div
                  v-if="showAbout"
                  class="tw-absolute tw-bottom-0 tw-right-0 tw-h-[2px] tw-w-full tw-bg-orange-500"
                />
              </Transition>
            </div>
            <button
              v-if="isInteractionsTabDisplayed"
              type="button"
              :class="{ noInteraction: !hasInteraction }"
              class="tw-relative tw-flex tw-w-full tw-items-center lg:tw-w-auto"
              @click="emit('toggle')"
            >
              <div
                class="tw-inline-block tw-w-full tw-cursor-pointer tw-p-6 tw-text-center lg:tw-w-auto lg:tw-py-8"
                :class="{ 'tw-cursor-default': !hasInteraction }"
              >
                <span
                  class="tw-text-body tw-inline-block"
                  :class="{
                    'tw-font-medium tw-text-orange-500': showInteraction,
                  }"
                  >{{ interactionSelectorText }}</span
                >
              </div>
              <Transition name="scale" mode="out-in">
                <div
                  v-if="showInteraction"
                  class="tw-absolute tw-bottom-0 tw-right-0 tw-h-[2px] tw-w-full tw-bg-orange-500"
                />
              </Transition>
            </button>
          </div>
          <BtnOrange v-if="userIsOwner" @click="leaveLink">
            {{ $t('influencer.profile.slug.profile.nav05') }}
          </BtnOrange>
        </div>
        <LazyVBookmark
          v-if="isBookmarkDisplayed"
          :influencer="influencer"
          :hide-tooltip-arrow="true"
        />
      </div>
    </ClientOnly>
  </div>
</template>
